import { Outlet, useLocation, useNavigate, useSearchParams } from 'react-router-dom'
import { useAppDispatch, useAppSelector } from './app/store/hooks/Redux'
import {
  hideAlert,
  hideModal,
  selectAlerts,
  selectModals,
  setLocation,
  showAlert,
} from './app/store/reducers/AppSlice'
import { BpAlert } from './shared/components/cards/alert/BpAlert'
import { BpModal } from './shared/components/cards/modal/BpModal'
import { useEffect, useState } from 'react'
import { useOutsideClick } from './shared/lib/hooks/UseOutsideClick'
import { useCookies } from 'react-cookie'
import { AiProvider } from './shared/components/ai/ai-provider/AiProvider'
import { AppliedFiltersProvider } from './lib/context/applied-filters/AppliedFiltersProvider'
import { getBaseDomain } from './shared/lib/helpers/GetBaseDomain'
import { useTranslation } from 'react-i18next'
import { selectAuthenticated, selectUser } from './app/store/reducers/AuthSlice'
import { usePostReferralClickMutation } from './app/api'

function App() {
  const { t } = useTranslation()
  const dispatch = useAppDispatch()
  const [searchParams, setSearchParams] = useSearchParams()
  const [cookies, setCookie] = useCookies(['referer', 'ref_cookie_id'])
  const alerts = useAppSelector(selectAlerts)
  const modals = useAppSelector(selectModals)
  const authenticated = useAppSelector(selectAuthenticated)
  const user = useAppSelector(selectUser)
  const location = useLocation()
  const body = document.querySelector('body')
  const [isShowAlerts, setIsShowAlerts] = useState<boolean>(true)
  const navigate = useNavigate()
  const clickRef = useOutsideClick(() => {
    if (alerts.length) setIsShowAlerts(false)
  })

  const [getClick] = usePostReferralClickMutation()

  useEffect(() => {
    if (body && modals?.length) {
      body.style.overflow = 'hidden'
    } else if (body) {
      body.style.overflow = 'auto'
    }
  }, [modals])

  useEffect(() => {
    dispatch(setLocation(location))
  }, [location])

  useEffect(() => {
    const hostname = window.location.hostname
    const referer = document.referrer

    if (referer) {
      const decoded = decodeURIComponent(referer)
      const url = new URL(decoded)

      if (url.host.includes(process.env.REACT_APP_PROJECT as string)) return

      setCookie('referer', referer, {
        path: '/',
        domain: `.${getBaseDomain(hostname)}`,
        maxAge: 86400,
      })
    }
  }, [cookies, setCookie])

  useEffect(() => {
    if (searchParams.has('payment')) {
      if (searchParams.get('payment') === 'success') {
        dispatch(
          showAlert({
            message: t('notifications.paymentSuccess'),
            timeout: 5000,
            type: 'success',
          }),
        )
      } else if (searchParams.get('payment') === 'cancel') {
        dispatch(
          showAlert({
            message: t('notifications.paymentFailure'),
            timeout: 5000,
            type: 'error',
          }),
        )
      }

      const newParams = new URLSearchParams(searchParams.toString())

      newParams.delete('payment')
      setSearchParams(newParams, { replace: true })
    }
  }, [searchParams])

  useEffect(() => {
    if (searchParams.has('ref')) {
      const id = searchParams.get('ref')

      if (authenticated !== null) {
        if (authenticated === false) {
          getClick({
            code: id as string,
            ...(cookies.ref_cookie_id && { refCookieId: cookies.ref_cookie_id }),
          })
            .unwrap()
            .then((response) => {
              if (response.success && response.data?.cookie_id) {
                setCookie('ref_cookie_id', response.data.cookie_id, {
                  path: '/',
                  maxAge: 30 * 24 * 60 * 60,
                })
              }
            })
            .catch(() => undefined)
            .finally(() => navigate('/auth/signup'))
        }
      }
    }
  }, [searchParams, authenticated])

  useEffect(() => {
    if (
      process.env.REACT_APP_PROJECT === 'bluepear' &&
      process.env.REACT_APP_BUILD_ENV === 'prod' &&
      !user?.is_virtual_mode
    ) {
      const cioanalytics = document.createElement('script')
      cioanalytics.innerHTML = `
      !function(){var i="cioanalytics", analytics=(window[i]=window[i]||[]);if(!analytics.initialize)if(analytics.invoked)window.console&&console.error&&console.error("Snippet included twice.");else{analytics.invoked=!0;analytics.methods=["trackSubmit","trackClick","trackLink","trackForm","pageview","identify","reset","group","track","ready","alias","debug","page","once","off","on","addSourceMiddleware","addIntegrationMiddleware","setAnonymousId","addDestinationMiddleware"];analytics.factory=function(e){return function(){var t=Array.prototype.slice.call(arguments);t.unshift(e);analytics.push(t);return analytics}};for(var e=0;e<analytics.methods.length;e++){var key=analytics.methods[e];analytics[key]=analytics.factory(key)}analytics.load=function(key,e){var t=document.createElement("script");t.type="text/javascript";t.async=!0;t.setAttribute('data-global-customerio-analytics-key', i);t.src="https://cdp-eu.customer.io/v1/analytics-js/snippet/" + key + "/analytics.min.js";var n=document.getElementsByTagName("script")[0];n.parentNode.insertBefore(t,n);analytics._writeKey=key;analytics._loadOptions=e};analytics.SNIPPET_VERSION="4.15.3";
        analytics.load(
          "61ff7c4ef607d8926183",
        );
      }}();`
      document.head.appendChild(cioanalytics)

      return () => {
        document.head.removeChild(cioanalytics)
      }
    }
  }, [])

  useEffect(() => {
    window.cioanalytics?.page({
      path: location.pathname,
      search: location.search,
      title: document.title,
    })
  }, [location])

  return (
    <>
      <AiProvider>
        <AppliedFiltersProvider>
          {alerts && (
            <div
              ref={clickRef}
              className='fixed right-0 top-0 z-[9999999] flex max-w-full flex-col gap-[10px] md:right-5 md:top-5 md:max-w-[320px]'
            >
              {alerts.map((alert, index: number) => (
                <BpAlert
                  {...alert}
                  key={index}
                  show={isShowAlerts}
                  onAfterClose={() => {
                    dispatch(hideAlert(alert.message))
                    setIsShowAlerts(true)
                  }}
                />
              ))}
            </div>
          )}
          {modals?.map(
            (modal, index) =>
              modal?.children && (
                <BpModal
                  key={index}
                  id={modal.id}
                  layer={index + 1}
                  scroll={modal.scroll}
                  size={modal.size}
                  width={modal.width}
                  withBackButton={modal.withBackButton}
                  onClose={() => {
                    if (modal.onClose) {
                      modal.onClose()
                    } else {
                      dispatch(hideModal())
                    }
                  }}
                >
                  {modal.children}
                </BpModal>
              ),
          )}
          <Outlet />
        </AppliedFiltersProvider>
      </AiProvider>
    </>
  )
}

export default App
